import React, { useState } from 'react';
import { Container, Form, Button, Image, Row, Col } from 'react-bootstrap';
import SideNav from '../components/SideNav';
import { useParams } from 'react-router-dom';
import { Tabs } from '@mantine/core';
import {
  IconClock, IconCurrencyRupee, IconCalendarEvent,
  IconPhoto, IconMessageCircle, IconSettings, IconPhone, IconHome, IconUser
} from '@tabler/icons-react';
import LeadOverview from '../components/LeadOverview';
import LeadTasks from '../components/LeadTasks';
import LeadHistory from '../components/LeadHistory';
import LeadDocuments from '../components/LeadDocuments';
import QuotesOverview from '../components/quotes/QuotesOverview';

// ── Import the hook we built ──────────────────────────────────────────────────
// useLead(leadId) returns the standard react-query shape:
//   { data, isLoading, isError, error }
// where `data` is already the lead object: { id, full_name, mobile_number, ... }
// because our queryFn does:  return response.data.data  (unwraps the API envelope)
import { useLead } from '../hooks/useLeads';

function LeadsDetails() {
  const { leadId } = useParams();

  const [activeTab, setActiveTab] = useState('overview');

  // ── useLead automatically:
  //    1. Fetches GET /api/leads/:leadId
  //    2. Joins the socket room  join:lead  → { leadId }
  //    3. Re-fetches whenever lead:updated / lead:won / lead:lost fires for THIS lead
  const { data: lead, isLoading, isError, error } = useLead(leadId);
  console.log("Lead details data:", lead, "Loading:", isLoading, "Error:", error);

 
  // ── Empty state ───────────────────────────────────────────────────────────
  // useLead returns undefined while loading, and the lead object once resolved.
  // This guard is just a safety net.


  // ── From here, `lead` is the flat lead object, e.g.:
  //    lead.full_name, lead.mobile_number, lead.propertyName, etc.
  //    NO more  data.data.full_name  — just  lead.full_name
  return (
    <Container fluid className='p-3' style={{ height: "100vh" }}>
      <Row>
        <Col className='col-auto'>
          <SideNav />
        </Col>

        <Col className='col-9' style={{ padding: "0px 10px 0px 10px", flexGrow: 1 }}>

          {/* ── Top bar ─────────────────────────────────────────────────── */}
          <Row
            className='p-2'
            style={{ backgroundColor: "#fff", borderRadius: '10px', alignItems: "center", justifyContent: "spaceBetween" }}
          >
            <Col><h4 style={{ color: "#000" }}>Lead Details</h4></Col>
            <Col className="d-flex justify-content-end align-items-center">
              <Image
                src={require('../assets/user.jpg')}
                roundedCircle
                style={{ width: '40px', height: 'auto' }}
              />
            </Col>
          </Row>

          {isLoading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              Loading lead details...
            </div>
          ) : isError ? (
            <div style={{ padding: '20px', color: '#ef4444' }}>
              Error loading lead details: {error.message}
            </div>
          ) : isError ? (
            <div>No Details found</div>
          ) : (
            /* ── Main content ─────────────────────────────────────────────── */
            <Row
              className='p-2 mt-2'
              style={{ backgroundColor: "#fff", borderRadius: '10px', justifyContent: "spaceBetween" }}
            >

            {/* ── Left sidebar — lead summary card ──────────────────────── */}
            {/* ── Left sidebar — hidden when quotes tab is active ── */}
            {activeTab !== 'quotes' && ( 
            <Col className='col-auto'>

              {/* Avatar + name + phone */}
              <div style={{
                backgroundColor: "#154889",
                borderTopLeftRadius: '15px', borderTopRightRadius: '15px',
                boxShadow: '0 8px 32px rgba(26,42,74,0.18)',
                padding: '12px 15px 1px 15px',
              }}>
                {/* Avatar initial — first letter of the lead's name */}
                <div style={{
                  width: '48px', height: '48px', background: '#f97316',
                  borderRadius: '50%', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '1.4rem', fontWeight: 700,
                  color: '#fff', marginBottom: '0.7rem',
                }}>
                  {lead.full_name?.charAt(0).toUpperCase() ?? 'L'}
                </div>

                {/* lead.full_name  ← was  data.data.full_name */}
                <h5 style={{ textTransform: "capitalize", color: '#ffffff' }}>
                  {lead.full_name}
                </h5>

                {/* lead.mobile_number  ← was  data.data.mobile_number */}
                <p style={{
                  width: '100%', color: '#ffffff', backgroundColor: '#275692',
                  padding: '3%', borderRadius: '10px',
                }}>
                  <IconPhone size={20} /> +91 {lead.mobile_number}
                </p>
              </div>

              {/* Info rows */}
              <div className='p-3' style={{
                borderBottomLeftRadius: '15px', borderBottomRightRadius: '15px',
                boxShadow: '0 8px 32px rgba(26,42,74,0.18)',
                padding: '8px 15px 5px 15px', marginBottom: '5%',
              }}>

                <div className="info-row">
                  <IconHome className="info-icon" />
                  <div>
                    <div className="info-label">Property Name</div>
                    {/* lead.propertyName  ← was  data.data.propertyName */}
                    <div className="info-value">{lead.propertyName ?? '—'}</div>
                  </div>
                </div>

                <div className="info-row">
                  <IconCalendarEvent className="info-icon" />
                  <div>
                    <div className="info-label">Possession Date</div>
                    {/* lead.possession  ← was  data.data.possession */}
                    <div className="info-value">{lead.possession ?? '—'}</div>
                  </div>
                </div>

                <div className="info-row">
                  <IconCurrencyRupee className="info-icon" />
                  <div>
                    <div className="info-label">Deal Size</div>
                    {/* lead.budget is the closest field in your schema to "deal size" */}
                    <div className="info-value">₹{lead.dealSize ?? '—'}</div>
                  </div>
                </div>

                <div className="info-row">
                  <IconClock className="info-icon" />
                  <div>
                    <div className="info-label">Tentative Closure Date</div>
                    {/* lead.meeting_date is the best available date field */}
                    <div className="info-value">{lead.meeting_date ?? '—'}</div>
                  </div>
                </div>

                <div className="info-row">
                  <IconUser className="info-icon" />
                  <div>
                    <div className="info-label">Sales Rep</div>
                    {/* lead.sales_rep is the best available field for sales representative */}
                    <div className="info-value">{lead.sales_rep ?? '—'}</div>
                  </div>
                </div>
              </div>

              {/* ── Scoped styles ──────────────────────────────────────── */}
              <style jsx>{`
                .info-row {
                  display: flex;
                  align-items: flex-start;
                  gap: 0.75rem;
                  margin-bottom: 1rem;
                }
                .info-row:last-child { margin-bottom: 0; }
                .info-icon {
                  color: #94a3b8;
                  font-size: 1rem;
                  margin-top: 2px;
                  flex-shrink: 0;
                }
                .info-label {
                  color: #94a3b8;
                  font-size: 0.7rem;
                  font-weight: 500;
                  letter-spacing: 0.03em;
                  margin-bottom: 1px;
                }
                .info-value {
                  color: #111;
                  font-size: 0.95rem;
                  font-weight: 600;
                }
              `}</style>
            </Col>
             )}  

            {/* ── Right side — tabbed content ────────────────────────────── */}
            <Col>
              <Tabs color="red" radius="xs"  value={activeTab}  onTabChange={(value) => setActiveTab(value)} >
                <Tabs.List grow>
                  <Tabs.Tab value="overview" leftSection={<IconPhoto size={12} />}>
                    Overview
                  </Tabs.Tab>
                  <Tabs.Tab value="tasks" leftSection={<IconMessageCircle size={12} />}>
                    Tasks
                  </Tabs.Tab>
                  <Tabs.Tab value="documents" leftSection={<IconSettings size={12} />}>
                    Documents
                  </Tabs.Tab>
                  <Tabs.Tab value="history" leftSection={<IconSettings size={12} />}>
                    History
                  </Tabs.Tab>
                  <Tabs.Tab value="quotes" leftSection={<IconSettings size={12} />}>
                    Quotes
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="overview">
                  {/*
                    Pass the flat `lead` object down.
                    LeadOverview previously received `data` (the full API envelope)
                    and accessed data.data.xyz — update LeadOverview to use
                    leadData.xyz directly now that we pass the unwrapped object.
                  */}
                  <LeadOverview leadData={lead} />
                </Tabs.Panel>

                <Tabs.Panel value="tasks">
                  {/* Pass leadId so LeadTasks can fetch its own tasks */}
                  <LeadTasks leadId={leadId} />
                </Tabs.Panel>

                <Tabs.Panel value="documents">
                  <LeadDocuments leadId={leadId} />
                </Tabs.Panel>

                <Tabs.Panel value="history">
                  <LeadHistory leadId={leadId} />
                </Tabs.Panel>

                 {/* <Tabs.Panel value="quotes">
                  <QuotesOverview leadId={leadId} />
                </Tabs.Panel> */}
              </Tabs>
            </Col>

          </Row> )}
        </Col>
      </Row>
    </Container>
  );
}

export default LeadsDetails;